<template>
  <b-container
    v-if="!isLoading"
    fluid
    class="p-0 page__learning-unit"
    :class="{ 'preview-mode': isPreviewMode }"
  >
    <!-- Add preview mode banner -->
    <div v-if="isPreviewMode" class="preview-banner">
      You are previewing method page as the learner role. To view the latest information,
      <a href="#" @click.prevent="refreshPage">Refresh</a> this page.
    </div>
    <b-row class="justify-content-md-center m-0 container-learning-unit">
      <div class="left-column">
        <!-- Add back navigation -->
        <component
          :is="isLargeScreen ? currentComponent : 'div'"
          mode="view"
          class="method-sidebar"
        >
          <div class="sidebar__content">
            <div v-if="!isPreviewMode" class="back-nav">
              <router-link to="/user-dashboard" class="back-link">
                <font-awesome-icon icon="chevron-left" />
                Back to User Dashboard
              </router-link>
            </div>
            <!-- Title and Progress Card -->
            <div class="content-card mb-u5">
              <h5 v-if="learningUnit" class="display-4 mb-2 text__mc-title">
                {{ learningUnit.name }}
              </h5>
              <learning-unit-progress
                v-if="learningUnit"
                :level-type="learningUnit.level_type"
                :method-components="methodComponents"
                :read-component-ids="readComponentIds"
              />
            </div>

            <!-- Competency Card -->
            <div class="content-card content__card-competency mb-u5">
              <img :src="competencyIcon" class="competency-icon" />
              <div ref="text__competency-title">
                <div class="text__competency-title">Competency</div>
                <base-text-editor-display>
                  <div
                    v-if="learningUnit && learningUnit.competency_detail"
                    v-safe-html="learningUnit.competency_detail"
                    class="text__competency text-pre-line"
                  ></div>
                </base-text-editor-display>
              </div>
            </div>

            <!-- Method Components Card -->
            <div
              v-if="methodComponents && methodComponents.length"
              class="content-card content-card-no-padding mb-u5 container__method-components"
            >
              <div class="section-title">
                <b-img :src="iconConiconBook" class="section-icon" />

                Method Components
              </div>
              <hr class="divider" />
              <div
                v-for="component in methodComponents"
                :key="component.id"
                class="list-item"
                :class="{ active: isActiveComponent(component.id) }"
                @click="goToMethodComponent(component.id)"
              >
                <div class="d-flex align-items-center">
                  <div
                    class="radio-circle"
                    :class="{ checked: readComponentIds.includes(component.id) }"
                  >
                    <font-awesome-icon
                      v-if="readComponentIds.includes(component.id)"
                      icon="check"
                      class="check-icon"
                    />
                  </div>
                  <span class="item-text text__sidebar-menu">{{ component.title }}</span>
                </div>
              </div>
            </div>

            <!-- Reference Card -->
            <div class="content-card content-card-no-padding container__reference">
              <div class="section-title">
                <b-img :src="iconConiconBook" class="section-icon" />
                Reference
              </div>
              <hr class="divider" />
              <div class="list-item" :class="{ active: isReferenceActive }" @click="goToReference">
                <div class="d-flex align-items-center">
                  <span class="item-text">Reference</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Submit Evidence Button -->
          <div class="submission-button">
            <div class="submission-button__content">
              <p class="mb-u5 font-weight-bold">Are you ready ?</p>

              <b-button
                variant="success"
                class="w-100 button__submit-your-evidence"
                :disabled="isPreviewMode"
                @click="openEvidenceModal"
              >
                <span v-if="isLoading">
                  <b-spinner small />
                </span>
                <span v-else>{{ evidenceActionText }}</span>
              </b-button>
            </div>
          </div>
        </component>
      </div>

      <div class="right-column">
        <b-row class="justify-content-md-center m-0">
          <b-col class="p-0">
            <router-view class="method__content"></router-view>
          </b-col>
        </b-row>
      </div>
    </b-row>

    <ModalSubmitEvidenceDetails
      :show-modal="showModal"
      @canceled="showModal = false"
      @confirmed="submitEvidenceAndCloseModal"
    />
  </b-container>
</template>

<script>
import { feedMixin } from '@/mixins/feedMixin';
import LearningUnit from '@/models/LearningUnit';
import { userInfoMixin } from '@/mixins';
import Application from '@/models/Application';
import BaseTextEditorDisplay from '@/components/base-components/BaseTextEditorDisplay';
import specificImage from '@/assets/learning-unit-types/specific.svg';
import academicImage from '@/assets/learning-unit-types/academic.svg';
import competencyIcon from '@/assets/icon-method-component/icon-competency.svg';
import MethodSidebar from '../LearningUnitMethods/components/MethodSidebar.vue';
import LearningUnitProgress from './components/LearningUnitProgress.vue';
import iconConiconBook from '@/assets/icon-method-component/icon-codicon-book.svg';
import { MethodComponent } from '@/models/MethodComponent';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import EvidenceSubmission from '@/models/EvidenceSubmission';
import ModalSubmitEvidenceDetails from '@/views/LearningUnitMethods/components/ModalSubmitEvidenceDetails.vue';

export default {
  components: {
    BaseTextEditorDisplay,
    MethodSidebar,
    LearningUnitProgress,
    FontAwesomeIcon,
    ModalSubmitEvidenceDetails
  },
  mixins: [feedMixin, userInfoMixin],

  props: {
    isPreview: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      readComponentIds: [],
      fetching: false,
      competencyIcon,
      isLargeScreen: true,
      currentComponent: 'MethodSidebar',
      iconConiconBook,
      selectedEvidenceSubmissionId: null,
      isLoading: true,
      showModal: false,
      learningUnit: null
    };
  },

  computed: {
    learningUnitId() {
      return Number.parseInt(this.$route.params.learningUnitId);
    },

    breadcrumbs() {
      const { learningUnit } = this;

      const { $route } = this;

      const breadcrumb = [
        {
          key: 'user-dashboard',
          name: 'User Dashboard',
          to: {
            name: 'user-dashboard'
          }
        },
        {
          key: 'micro-credentials-detail',
          name: learningUnit ? learningUnit.name : '',
          to: {
            name: 'micro-credentials/detail',
            params: {
              learningUnitId: learningUnit ? learningUnit.id : null
            }
          }
        }
      ];

      if ($route.name === 'learning-unit/verify') {
        breadcrumb.push({
          key: 'verify',
          name: 'Verify',
          active: true
        });
      }

      if ($route.name === 'learning-unit/feed') {
        breadcrumb.push({
          key: 'learning-feed',
          name: 'Learning',
          active: true
        });
      }

      return breadcrumb;
    },

    isPageVerifyCompetency() {
      return this.$route.name === 'learning-unit/verify';
    },

    pageSubmitEvidence() {
      return {
        name: 'learning-unit/verify',
        query: {
          tab: 'submission'
        },
        params: {
          learningUnitId: this.learningUnit?.id
        }
      };
    },

    pageOverAllDenied() {
      return {
        name: 'learning-unit/submission-denied',
        params: {
          learningUnitId: this.learningUnit?.id,
          evidenceSubmissionId: this.selectedEvidenceSubmissionId
        }
      };
    },

    applied() {
      return Application.query()
        .where((apply) => {
          return apply.user.id == this.userId && apply.learning_unit.id == this.learningUnitId;
        })
        .first();
    },

    isModeLearnVerify() {
      return this.applied ? this.applied.isModeLearnVerify : false;
    },

    levelTypeImage() {
      return this.learningUnit.level_type === 'CAREER_SPECIFIC' ? specificImage : academicImage;
    },

    isPreviewMode() {
      return this.isPreview;
    },

    methodComponents() {
      return MethodComponent.query().where('learning_unit_id', this.learningUnitId).get();
    },

    currentMethodComponentId() {
      if (this.$route.query.methodComponent) {
        return Number(this.$route.query.methodComponent);
      }
      return this.methodComponents?.[0]?.id || null;
    },

    routeNames() {
      const base = this.isPreview ? '-preview' : '';
      return {
        methodComponents: `learning-unit/method-components${base}`,
        references: `learning-unit/references${base}`
      };
    },

    isReferenceActive() {
      return this.$route.name?.startsWith('learning-unit/references');
    },

    evidenceSubmission() {
      return this.selectedEvidenceSubmissionId
        ? EvidenceSubmission.find(this.selectedEvidenceSubmissionId)
        : null;
    },

    evidenceActionText() {
      return this.evidenceSubmission && this.evidenceSubmission?.status === 'NOT_SUBMITTED'
        ? 'Submit your evidence'
        : 'View your evidence';
    }
  },

  watch: {
    $route: {
      immediate: true,
      handler: 'onRouteChanged'
    },
    learningUnitId: {
      immediate: true,
      handler: 'fetchMethodComponents'
    }
  },

  async created() {
    await this.fetchMethodComponentProgress();
    await this.fetchEvidenceSubmission();
  },

  mounted() {
    this.$root.$on('method-component-read', this.fetchMethodComponentProgress);
    this.updateView();
    window.addEventListener('resize', this.updateView);
  },

  beforeDestroy() {
    this.$root.$off('method-component-read', this.fetchMethodComponentProgress);
    window.removeEventListener('resize', this.updateView);
  },

  methods: {
    updateView() {
      this.isLargeScreen = window.innerWidth <= 992;
    },

    goToSignInPageWithRedirect() {
      const previousRoute = this.$router.resolve({
        ...this.$route,
        params: {
          learningUnitId: this.learningUnitId
        }
      });

      this.$router.push({
        name: 'SignIn',
        query: {
          previousPath: previousRoute.href
        }
      });
    },

    async onRouteChanged() {
      if (!this.user) {
        return this.goToSignInPageWithRedirect();
      }

      try {
        // Fetch the application data
        if (!this.applied) {
          await this.fetchApply();
        }

        // If the user has not applied for the learning unit, redirect to the learning unit page
        if (!this.applied && !this.isPreviewMode) {
          return this.$router.push({ name: 'not-found' });
        }

        // Only fetch learning unit data if it hasn't been loaded yet
        if (!this.learningUnit) {
          await this.fetch();
        }

        if (!this.methodComponents.length) {
          await this.fetchMethodComponents();
        }

        // Handle the method component redirect
        this.handleMethodComponentRedirect();
      } catch (error) {
        console.error('Error in route change handling:', error);
      }
    },

    handleMethodComponentRedirect() {
      const isMethodComponentsRoute = this.$route.name?.startsWith(
        'learning-unit/method-components'
      );
      const hasNoMethodComponent = !this.$route.query.methodComponent;
      const hasComponents = this.methodComponents?.length > 0;

      // Redirect to first component if we're on method components route with no specific component selected
      if (isMethodComponentsRoute && hasNoMethodComponent && hasComponents) {
        this.$router.replace(
          this.getRouteConfig('methodComponents', {
            query: { methodComponent: this.methodComponents[0].id }
          })
        );
      }
    },

    async fetch() {
      try {
        const learningUnit = await LearningUnit.api().fetchByLearningUnitId(this.learningUnitId);
        this.learningUnit = learningUnit.response.data;
      } catch (error) {
        this.$toast('Cannot load learning unit, please try again');
      }
    },

    async fetchApply() {
      if (!this.userId) return;
      try {
        await Application.api().find({
          user: this.userId,
          learning_unit: this.learningUnitId
        });
      } catch (error) {
        this.$toast('Cannot load application, please try again');
      } finally {
        this.fetching = false;
      }
    },

    isActiveComponent(componentId) {
      // Return false if we're on the reference page
      if (this.isReferenceActive) {
        return false;
      }
      return this.currentMethodComponentId === componentId;
    },

    getRouteConfig(routeName, params = {}) {
      return {
        name: this.routeNames[routeName],
        params: {
          learningUnitId: this.learningUnitId,
          ...params
        },
        query: {
          ...params.query
        }
      };
    },

    goToMethodComponent(componentId) {
      this.$router.push(
        this.getRouteConfig('methodComponents', {
          query: { methodComponent: componentId }
        })
      );
    },

    goToReference() {
      this.$router.push(this.getRouteConfig('references'));
    },

    async fetchMethodComponentProgress() {
      try {
        const response = await LearningUnit.api().fetchMethodComponentsProgress(
          this.learningUnitId
        );
        this.readComponentIds = response.response.data;
      } catch (error) {
        console.error('Error fetching method component progress:', error);
      }
    },

    async fetchMethodComponents() {
      if (!this.learningUnitId) return;

      try {
        const response = await MethodComponent.api().fetch({
          learning_unit: this.learningUnitId
        });

        // Wait for the store to be updated
        await this.$nextTick();

        return response;
      } catch (error) {
        console.error('Error fetching method components:', error);
        this.$toast('Cannot load method components, please try again');
        throw error;
      }
    },

    openEvidenceModal() {
      if (this.evidenceSubmission && this.evidenceSubmission.status === 'NOT_SUBMITTED') {
        this.showModal = true;
      } else {
        this.submitEvidenceAndCloseModal();
      }
    },

    submitEvidenceAndCloseModal() {
      const evidenceUrl = this.evidenceSubmission?.isDenied
        ? this.$router.resolve(this.pageOverAllDenied).href
        : this.$router.resolve(this.pageSubmitEvidence).href;

      window.open(evidenceUrl, '_blank');

      this.showModal = false;
    },

    refreshPage() {
      window.location.reload();
    },

    async fetchEvidenceSubmission() {
      this.isLoading = true;

      try {
        await EvidenceSubmission.api().fetchLearner(this.learningUnitId);

        const latestEvidenceSubmission = EvidenceSubmission.getLatestEvidenceSubmission(
          this.learningUnitId,
          this.userId
        );

        if (latestEvidenceSubmission) {
          this.selectedEvidenceSubmissionId = latestEvidenceSubmission.id;
        }
      } catch (error) {
        this.$toast('Cannot load your submissions, please try again');
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss">
.page__learning-unit {
  //sarabun Font Only at PageIndexV2.vue
  @import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@400;500;600;700&display=swap');

  font-family: 'Sarabun', sans-serif;
  flex: 1;

  &:not(.preview-mode) {
    margin-top: -1.5em;
  }

  .container-learning-unit {
    display: grid;
    grid-template-columns: 352px 1fr;
    width: 100%;
  }

  .btn__start-learning {
    max-width: 248px;
    width: 100%;
  }

  .text__mc-title {
    font-size: 20px;
    font-weight: 600;
    line-height: 26px;
  }

  .text__competency-title {
    font-size: 16px;
    font-weight: 700;
    line-height: 20.8px;
  }

  .text__competency {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }

  .ql-editor.ql-editor--display {
    padding: 0;
    margin: 0;
  }

  .left-column {
    background-color: #eceef7;
    border-right: 1px solid #d1d2db;
    height: 100%;

    .method-sidebar {
      height: calc(100dvh - 76px); // 76px is the height of Navbar
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .sidebar__content {
      padding: 20px;
      flex-grow: 1;
      overflow-y: auto;
    }

    .content-card {
      max-width: 352px;
      background: white;
      border-radius: 12px;
      padding: 16px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .content-card-no-padding {
      padding: 20px 0;
    }

    .content__card-competency {
      display: flex;
      gap: 8px;
    }
  }

  .section-title {
    padding: 0 16px 16px 16px;
    display: flex;
    align-items: center;
    font-weight: bold;

    .section-icon {
      width: 24px;
      height: 24px;
      margin-right: 8px;
    }
  }

  .list-item {
    padding: 12px 16px;
    background-color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    border: 1px solid transparent;
    color: #3c44b1;

    &:hover {
      background-color: #f8f9fa;
    }

    &.active {
      background: linear-gradient(91.61deg, #667ce8 0%, #744ba3 100%);
      color: white;

      .radio-circle {
        border-color: white;
        background-color: transparent;

        &.checked {
          border: none;

          &:after {
            color: white;
          }
        }
      }
    }

    .radio-circle {
      min-width: 20px;
      height: 20px;
      border: 1px solid #3c44b1;
      border-radius: 50%;
      margin-right: 12px;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;

      &.checked {
        border: none;
        background-color: transparent;
      }

      .check-icon {
        color: #1bc943;
        font-size: 16px;
      }
    }

    .item-text {
      font-size: 16px;
      font-weight: 600;
    }
  }

  .submission-button {
    background-color: #ffffff;
    border-top: 1px solid #d1d2db;
    position: sticky;
    bottom: 0px;

    .submission-button__content {
      padding: 20px;
    }
  }

  .divider {
    margin: 0;
    padding: 0;
    border: none;
    border-top: 1px solid #e0e0e0;
  }

  .back-nav {
    padding-bottom: 20px;
  }

  .back-link {
    color: var(--primary);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: none;
      color: #4338ca;
    }

    i {
      margin-right: 8px;
    }
  }

  @media screen and (max-width: 992px) {
    .container-learning-unit {
      grid-template-columns: 1fr;
      height: max-content;
    }

    .left-column {
      padding: 24px 24px 0 24px;
      background-color: transparent;
    }
  }

  .competency-icon {
    height: 20px;
  }

  .preview-banner {
    background-color: #3b3e66;
    color: white;
    padding: 8px 16px;
    width: 100%;

    a {
      color: white;
      text-decoration: underline;

      &:hover {
        color: #e0e0e0;
      }
    }
  }

  .method__content {
    height: calc(100dvh - 76px); // 76px is the height of the Navbar
    overflow-y: auto;
  }
}

@media screen and (max-width: 1200px) {
  .page__learning-unit {
    .left-column .method-sidebar {
      height: calc(100dvh - 72px); // 72px is the height of the Navbar when below 1200px of screen.
    }

    .right-column .method__content {
      height: calc(100dvh - 72px); // 72px is the height of the Navbar when below 1200px ofscreen.
    }
  }
}

@media screen and (max-width: 992px) {
  .page__learning-unit {
    display: block;

    .left-column .method-sidebar {
      height: max-content;
    }

    .right-column .method__content {
      height: max-content;
    }

    .sidebar__content {
      height: calc(100% - 130px); // 130px is the height of the Submit Evidence button
    }
  }
}

@media screen and (min-width: 992px) {
  .preview-mode {
    .left-column .method-sidebar {
      height: calc(100dvh - 40px); //40px is the height of the preview-banner
    }

    .right-column .method__content {
      height: calc(100dvh - 40px); //40px is the height of the preview-banner
    }
  }
}
</style>
